import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Menu, X, ShoppingCart, User, LogOut } from 'lucide-react';

const LOGO_URL = "/logo.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Art Club', path: '/art-club' },
    { name: 'Workshops', path: '/workshops' },
    { name: 'Shop', path: '/shop' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="bg-[#FDF6E8] border-b-2 border-slate-900 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-32">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Artsy Craftsy" 
              className="h-28 w-auto object-contain group-hover:scale-105 transition-transform"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="font-body font-semibold text-slate-700 hover:text-blue-600 transition-colors"
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center gap-4">
            <Link 
              to="/shop" 
              className="relative p-2 hover:bg-white/50 rounded-full transition-colors"
              data-testid="cart-btn"
            >
              <ShoppingCart className="w-6 h-6 text-slate-700" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-pink-500 text-white text-xs font-bold rounded-full flex items-center justify-center border border-slate-900">
                  {itemCount}
                </span>
              )}
            </Link>

            {user ? (
              <div className="flex items-center gap-3">
                <Link
                  to={user.is_admin ? '/admin' : '/portal'}
                  className="btn-primary text-sm py-2 px-4"
                  data-testid="portal-btn"
                >
                  <User className="w-4 h-4 inline mr-1" />
                  {user.is_admin ? 'Admin' : 'My Portal'}
                </Link>
                <button
                  onClick={handleLogout}
                  className="p-2 hover:bg-white/50 rounded-full transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5 text-slate-600" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="btn-outline text-sm py-2 px-4"
                  data-testid="login-btn"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="btn-primary text-sm py-2 px-4"
                  data-testid="register-btn"
                >
                  Register
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="font-body font-semibold text-slate-700 hover:text-blue-600 py-2 px-4 hover:bg-white/50 rounded-lg transition-colors"
                  onClick={() => setIsOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <hr className="my-2" />
              {user ? (
                <>
                  <Link
                    to={user.is_admin ? '/admin' : '/portal'}
                    className="btn-primary text-center"
                    onClick={() => setIsOpen(false)}
                  >
                    {user.is_admin ? 'Admin Dashboard' : 'Parent Portal'}
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setIsOpen(false); }}
                    className="btn-outline text-center"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="btn-outline text-center"
                    onClick={() => setIsOpen(false)}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="btn-primary text-center"
                    onClick={() => setIsOpen(false)}
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
